import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';
import { showSuccessThunk } from '@spike/notifications-action';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Select, SelectOption } from 'components/UI/V3/Forms/Select/Select';
import { useAuth } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchIdogcamIntegration } from '../api/IdogcamApi';
import IdogcamStatusBadge from '../IdogcamStatusBadge/IdogcamStatusBadge';
import { connectIdogcam, IdogcamInvalidCredentialsError, IdogcamUnavailableError } from '../lib/connectIdogcam';
import { IdogcamCredentials } from '../model/IdogcamModel';
import styles from './IdogcamConnectForm.module.scss';

export interface IdogcamConnectFormLocation {
    id: number;
    name: string;
}

interface IdogcamConnectFormProps {
    // Locations of the organization; the Location field is only shown when there is more than one.
    locations: Array<IdogcamConnectFormLocation>;
    initialLocationId: number;
    // Called once the location is connected and its cameras synced.
    onConnected: (locationId: number) => void;
    // Omitted when there is nothing to go back to (the screen's location is not connected).
    onCancel?: () => void;
}

const EMPTY_CREDENTIALS: IdogcamCredentials = { kennelId: '', erpcode: '' };

const connectionErrorMessage = (error: unknown): string => {
    if (error instanceof IdogcamInvalidCredentialsError) {
        return "We couldn't connect to iDogCam with these details. Check your Kennel ID and ERP Code and try again.";
    }
    if (error instanceof IdogcamUnavailableError) {
        return 'iDogCam is not available right now. Please try again later.';
    }
    return "We couldn't save the connection. Please try again.";
};

// Connection settings of any location of the organization. Changing the Location field loads that
// location's connection in place (prefilled when connected, empty when not); unsaved values of the
// previous location are discarded.
export const IdogcamConnectForm: FunctionComponent<IdogcamConnectFormProps> = props => {
    const auth = useAuth();
    const dispatch = useDispatch();

    const [locationId, setLocationId] = useState(props.initialLocationId);
    // Credentials stored for the selected location; null when it has none.
    const [savedCredentials, setSavedCredentials] = useState<IdogcamCredentials | null>(null);
    const [connected, setConnected] = useState(false);
    const [credentials, setCredentials] = useState<IdogcamCredentials>(EMPTY_CREDENTIALS);
    const [loadingLocation, setLoadingLocation] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoadingLocation(true);
        setError(null);

        fetchIdogcamIntegration(locationId, auth.token!)
            .then(integration => {
                if (cancelled) return;
                const stored =
                    integration.kennelId && integration.erpcode
                        ? { kennelId: integration.kennelId, erpcode: integration.erpcode }
                        : null;
                setSavedCredentials(stored);
                setConnected(integration.configured);
                setCredentials(stored ?? EMPTY_CREDENTIALS);
            })
            .catch(() => {
                if (cancelled) return;
                setSavedCredentials(null);
                setConnected(false);
                setCredentials(EMPTY_CREDENTIALS);
                setError("We couldn't load this location's connection. Please try again.");
            })
            .finally(() => {
                if (!cancelled) setLoadingLocation(false);
            });

        return () => {
            cancelled = true;
        };
    }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

    const locationName = props.locations.find(location => location.id === locationId)?.name;
    const trimmed: IdogcamCredentials = { kennelId: credentials.kennelId.trim(), erpcode: credentials.erpcode.trim() };
    const isComplete = trimmed.kennelId.length > 0 && trimmed.erpcode.length > 0;
    const isChanged =
        trimmed.kennelId !== (savedCredentials?.kennelId ?? '') ||
        trimmed.erpcode !== (savedCredentials?.erpcode ?? '');
    const isBusy = loadingLocation || submitting;
    const canSubmit = isComplete && isChanged && !isBusy;

    const locationOptions: Array<SelectOption<number>> = props.locations.map(location => ({
        label: location.name,
        value: String(location.id),
        data: location.id
    }));

    const changeLocationHandler = (value: string) => {
        const selectedLocationId = Number(value);
        if (selectedLocationId !== locationId) {
            setLocationId(selectedLocationId);
        }
    };

    const submitHandler = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            await connectIdogcam({
                marketplaceId: locationId,
                token: auth.token!,
                credentials: trimmed,
                previousCredentials: savedCredentials
            });
            const target = locationName ? ` for ${locationName}` : '';
            dispatch(showSuccessThunk(connected ? `Connection updated${target}` : `iDogCam connected${target}`));
            props.onConnected(locationId);
        } catch (submitError) {
            setError(connectionErrorMessage(submitError));
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.texts}>
                <div className={styles.titleRow}>
                    <h3 className={styles.title}>iDogCam connection</h3>
                    {!loadingLocation && (
                        <IdogcamStatusBadge
                            active={connected}
                            label={connected ? 'Connected' : 'Not connected'}
                        />
                    )}
                </div>
                <p className={styles.description}>
                    Enter the Kennel ID and ERP Code provided by iDogCam to make your cameras available in Cuddles.
                </p>
            </div>

            <div className={styles.fields}>
                {locationOptions.length > 1 && (
                    <Select
                        label="Location"
                        required={true}
                        options={locationOptions}
                        value={String(locationId)}
                        disabled={submitting}
                        onChange={changeLocationHandler}
                    />
                )}
                <Input
                    label="Kennel ID"
                    required={true}
                    placeholder={loadingLocation ? 'Loading...' : 'Kennel ID'}
                    value={credentials.kennelId}
                    disabled={isBusy}
                    autoComplete="off"
                    onChange={event => setCredentials(prev => ({ ...prev, kennelId: event.target.value }))}
                    onEnterPressed={submitHandler}
                />
                <Input
                    label="ERP Code"
                    required={true}
                    placeholder={loadingLocation ? 'Loading...' : 'ERP Code'}
                    value={credentials.erpcode}
                    disabled={isBusy}
                    autoFocus={false}
                    autoComplete="off"
                    onChange={event => setCredentials(prev => ({ ...prev, erpcode: event.target.value }))}
                    onEnterPressed={submitHandler}
                />
            </div>

            {error && (
                <AlertMessage
                    icon={faCircleExclamation}
                    bdColor="#EF4F57"
                    bgColor="#FBB9BC33"
                >
                    <span className={styles.alertText}>{error}</span>
                </AlertMessage>
            )}

            <div className={styles.actions}>
                {props.onCancel && (
                    <Button
                        label="Cancel"
                        variant="outlined"
                        size="large"
                        disabled={submitting}
                        className={styles.button}
                        onClick={props.onCancel}
                    />
                )}
                <Button
                    label={connected ? 'Save changes' : 'Connect'}
                    variant="green"
                    size="large"
                    disabled={!canSubmit}
                    isLoading={submitting}
                    className={styles.button}
                    onClick={submitHandler}
                />
            </div>
        </div>
    );
};

export default IdogcamConnectForm;
